import type { Component } from 'vue';
import ChildIntroStep from '../steps/ChildIntroStep.vue';
import ChallengesStep from '../steps/ChallengesStep.vue';
import ParentFeelingsStep from '../steps/ParentFeelingsStep.vue';
import GoalsStep from '../steps/GoalsStep.vue';
import BaseStep from '../steps/BaseStep.vue';
import type { Challenge, Feeling, Goal } from '../types';

export interface StepConfig {
  id: number;
  component: Component;
  props?: Record<string, unknown>;
  validation?: (formData: Record<string, unknown>) => boolean;
}

// Challenge options data
export const challengeOptions: Challenge[] = [
  { id: 'concentration', title: 'Konzentrationsprobleme', description: 'Schwierigkeiten, sich auf Aufgaben zu konzentrieren' },
  { id: 'motivation', title: 'Motivationsprobleme', description: 'Wenig Interesse am Lernen' },
  { id: 'organization', title: 'Organisationsprobleme', description: 'Schwierigkeiten mit Zeitmanagement und Planung' },
  { id: 'comprehension', title: 'Verständnisprobleme', description: 'Schwierigkeiten, den Lernstoff zu verstehen' },
  { id: 'memory', title: 'Gedächtnisprobleme', description: 'Schwierigkeiten, sich Informationen zu merken' },
  { id: 'anxiety', title: 'Prüfungsangst', description: 'Nervosität und Angst vor Tests und Prüfungen' },
  { id: 'none', title: 'Keine besonderen Herausforderungen', description: 'Mein Kind hat keine besonderen Schwierigkeiten beim Lernen' },
];

// Feeling options data
export const feelingOptions: Feeling[] = [
  { id: 'worried', text: 'Besorgt - Ich mache mir Sorgen um die Entwicklung meines Kindes' },
  { id: 'frustrated', text: 'Frustriert - Ich weiß nicht, wie ich helfen kann' },
  { id: 'hopeful', text: 'Hoffnungsvoll - Ich glaube, dass wir eine Lösung finden werden' },
  { id: 'curious', text: 'Neugierig - Ich möchte mehr über den Lerntyp meines Kindes erfahren' },
  { id: 'overwhelmed', text: 'Überfordert - Die Situation ist sehr belastend' },
];

// Goal options data
export const goalOptions: Goal[] = [
  { id: 'understand', title: 'Lerntyp verstehen', description: 'Den Lerntyp meines Kindes besser verstehen' },
  { id: 'improve_grades', title: 'Noten verbessern', description: 'Die schulischen Leistungen meines Kindes verbessern' },
  { id: 'reduce_stress', title: 'Stress reduzieren', description: 'Den Lernstress für mein Kind reduzieren' },
  { id: 'motivation', title: 'Motivation steigern', description: 'Die Lernmotivation meines Kindes steigern' },
  { id: 'strategies', title: 'Lernstrategien', description: 'Effektive Lernstrategien für mein Kind finden' },
  { id: 'independence', title: 'Selbstständigkeit fördern', description: 'Mein Kind zu mehr Selbstständigkeit beim Lernen befähigen' },
];

// Define the steps configuration
export const stepsConfig: StepConfig[] = [
  {
    id: 1,
    component: ChildIntroStep,
    validation: (formData) => {
      return Boolean((formData.childName as string)?.trim()) &&
             formData.childAge !== null &&
             Boolean((formData.childSchool as string)?.trim()) &&
             Boolean((formData.childSituation as string)?.trim());
    }
  },
  {
    id: 2,
    component: ChallengesStep,
    props: { challenges: challengeOptions },
    validation: (formData) => (formData.challenges as string[])?.length > 0
  },
  {
    id: 3,
    component: ParentFeelingsStep,
    props: { feelings: feelingOptions },
    validation: (formData) => (formData.parentFeelings as string[])?.length > 0
  },
  {
    id: 4,
    component: GoalsStep,
    props: { goals: goalOptions, maxSelections: 3 },
    validation: (formData) => (formData.selectedGoals as string[])?.length > 0
  },
  {
    id: 5,
    component: BaseStep,
    props: {
      title: "Bestätigung",
      description: "Vielen Dank für Ihre Angaben. Wir können jetzt mit dem Lerntypen-Test fortfahren."
    }
  }
];

// Helper functions
export function getStepById(id: number): StepConfig | undefined {
  return stepsConfig.find(step => step.id === id);
}

export function getTotalSteps(): number {
  return stepsConfig.length;
}

export function validateStep(stepId: number, formData: Record<string, unknown>): boolean {
  const step = getStepById(stepId);
  if (!step || !step.validation) return true;
  return step.validation(formData);
}
